/**
 * #261 F3 — the ONE place a self-reported wallet/connector name is interpreted.
 *
 * ── WHY THIS MODULE EXISTS ───────────────────────────────────────────────────────────────────────
 * A connector's `name` is SELF-REPORTED (`useWallet.ts:66` = wagmi's `connector?.name`; `:69` =
 * `solanaWallet?.adapter.name`; Tron's comes from `useTronWallet().activeTronWallet`). It is a
 * display hint, not an identity. Before F3 there were TWO places that turned one into a name — the
 * local `getConnectorDisplayName` in `WalletModal` and nothing at all in the review card, which
 * asserted a flat `(your wallet)` for every connected recipient. F3 adds a second consumer (the
 * review-card recipient label) and a third (the SwapCard brand icon), so the mapping is centralised
 * here rather than copied a third time.
 *
 * ── ALLOWLIST, NEVER ASSERTIVE (H6) ──────────────────────────────────────────────────────────────
 * An unmapped name — `Injected`, `Rabby Wallet`, a private-label build — yields NO name. It never
 * yields a guess. Naming the wrong provider on the last screen before signing is worse than naming
 * none: the whole point of F3 is that the user recognises which app holds the funds, and a confident
 * wrong answer defeats that more thoroughly than the generic copy it replaces.
 *
 * ── FAIL CLOSED ON ADDRESS-SHAPED INPUT (H3) ─────────────────────────────────────────────────────
 * Everything here refuses an address-shaped input. The recipient row is the #143 surface: the string
 * it renders must be byte-identical to the string that gets signed. The label sits directly beneath
 * that string, so if a second, looser recipient string were ever wired into the label path the row
 * would show two addresses and the user would have no way to know which one is authoritative. The
 * refusal is structural (`looksLikeAddress` below), not a convention — see `isRenderableWalletLabel`,
 * which the modal applies to the prop it is handed regardless of who built it.
 *
 * ── NO REACT, NO SIDE EFFECTS ────────────────────────────────────────────────────────────────────
 * Pure functions only, so every rule here is testable without a DOM and the brand-collision rule can
 * be pinned against INJECTED tables (H5) — with today's real tables that rule is unreachable, so a
 * test against them would assert nothing.
 */
import { CHAIN_REGISTRY } from '@/config/chains'
import type { Chain } from '@/types'

/**
 * Raw-name substring (lowercased) → canonical display name.
 *
 * The canonical name is ALSO the `WALLET_LOGO_KEY` key (`components/icons/WalletLogos.tsx:75-83`),
 * which is what lets `walletLogoKind` below be a straight pass-through rather than a second table.
 * `walletLabel.test.ts` pins that correspondence, so adding an entry here without an asset (or
 * renaming an asset key) fails rather than silently degrading to the generic glyph.
 *
 * Substring + case-insensitive, matching what `WalletModal`'s local `getConnectorDisplayName` did
 * before this module existed: EIP-6963 announces names like `io.metamask` / `MetaMask Wallet`, and
 * an exact-match table would miss them and fall to "your connected wallet" for the single most
 * common wallet on the app.
 *
 * ORDER IS SIGNIFICANT — first match wins. The entries are mutually exclusive on every name we have
 * observed (`coinbase wallet` does not contain `walletconnect`, and vice versa), but the iteration
 * order is what makes that a property of this list rather than an accident of object key order.
 */
export const WALLET_NAME_ALLOWLIST: ReadonlyArray<readonly [needle: string, canonical: string]> = [
  ['metamask', 'MetaMask'],
  ['coinbase', 'Coinbase Wallet'],
  ['walletconnect', 'WalletConnect'],
  ['phantom', 'Phantom'],
  ['solflare', 'Solflare'],
  ['backpack', 'Backpack'],
  ['tronlink', 'TronLink'],
]

/** The transport-not-a-wallet case, called out by name because three rules below branch on it. */
export const WALLETCONNECT_NAME = 'WalletConnect'

const EVM_ADDRESS_RE = /^0x[0-9a-fA-F]{40}$/
const TRON_ADDRESS_RE = /^T[1-9A-HJ-NP-Za-km-z]{33}$/
const BASE58_ADDRESS_RE = /^[1-9A-HJ-NP-Za-km-z]{32,44}$/

/**
 * H3 — is this token shaped like a destination address on any family we support?
 *
 * Deliberately covers all three families rather than just EVM: the recipient row renders Solana and
 * Tron recipients too, and a mis-wire that fed the Solana recipient into the label would sail past an
 * EVM-only check. The base58 window (32-44) subsumes the Tron form; both are kept so a reader can see
 * which shape each rule is about.
 *
 * No wallet NAME we map is 32+ characters of unbroken base58, so this cannot swallow a legitimate
 * provider name.
 *
 * ── TRUNCATED FORMS COUNT TOO (fixer pass, F-2) ──────────────────────────────────────────────────
 * `shortAddr` (`utils/format.ts`) renders an address as `0x7d775A…A149D02D` — 8 chars, a `…`
 * ellipsis, 8 chars. That shape matches none of the three regexes below (the ellipsis breaks every
 * pattern), so without this check a TRUNCATED address sailed straight through as if it were an
 * ordinary short string — the most likely accidental mis-wire of all, since `shortAddr(recipientAddress)`
 * is rendered two lines above the label in the same JSX block (`ReviewTransferModal/index.tsx`). Any
 * ellipsis — the `…` glyph `shortAddr` emits, or a literal `...` — is refused outright: no wallet
 * name we map contains one, so this cannot reject a legitimate provider name (pinned in
 * `walletLabel.test.ts`).
 */
export function looksLikeAddress(value: string): boolean {
  const t = value.trim()
  if (!t) return false
  if (t.includes('…') || t.includes('...')) return true
  return EVM_ADDRESS_RE.test(t) || TRON_ADDRESS_RE.test(t) || BASE58_ADDRESS_RE.test(t)
}

/**
 * The allowlist lookup. Returns the canonical display name, or `null` when the name is unmapped,
 * empty, or address-shaped.
 *
 * `null` is the honest answer, not a degraded one — every caller has its own correct fallback
 * (generic glyph, `your connected wallet`, or the raw name for the connect list) and none of them
 * should be inferring a provider from a name this function declined to recognise.
 *
 * @param raw the self-reported connector / adapter name
 * @param allowlist test seam (H5) — defaults to the module allowlist above
 */
export function canonicalWalletName(
  raw: string | null | undefined,
  allowlist: ReadonlyArray<readonly [string, string]> = WALLET_NAME_ALLOWLIST,
): string | null {
  if (!raw) return null
  const trimmed = raw.trim()
  if (!trimmed) return null
  // H3 first: an address must never reach the substring match, however improbable a collision is.
  if (looksLikeAddress(trimmed)) return null
  const lower = trimmed.toLowerCase()
  for (const [needle, canonical] of allowlist) {
    if (lower.includes(needle)) return canonical
  }
  return null
}

/**
 * The `walletKind` a `WalletChip` / `WalletModal` row needs for its brand logo.
 *
 * Reduces to `canonicalWalletName` because the canonical name IS the `WALLET_LOGO_KEY` key — the
 * existing house seam (`WalletChip/index.tsx:44-45`: `WALLET_LOGO_KEY[walletKind]` →
 * `WalletLogos[key]`). Named separately anyway so call sites read as what they want, and so a future
 * divergence between "the name we print" and "the asset we have" has one place to live.
 *
 * `null` → the caller falls back to the generic glyph, which is correct for an unknown provider.
 */
export function walletLogoKind(raw: string | null | undefined): string | null {
  return canonicalWalletName(raw)
}

/**
 * The connect-list display name (`WalletModal`).
 *
 * BEHAVIOUR-PRESERVING replacement for that file's former local `getConnectorDisplayName`: it mapped
 * metamask / phantom / coinbase / walletconnect by lowercase substring and passed everything else
 * through unchanged, which is exactly `canonicalWalletName(raw) ?? raw` for those four needles.
 *
 * The three extra needles this module adds (solflare / backpack / tronlink) cannot change any
 * rendered name there: `WalletModal` filters its EVM rows to `EVM_DISPLAY_ORDER`
 * (`['MetaMask', 'Phantom', 'Coinbase Wallet', 'WalletConnect']`), so a connector normalising to any
 * other value is dropped before it renders under BOTH the old function and this one. Pinned in
 * `walletLabel.test.ts` rather than left as an argument.
 */
export function walletDisplayName(raw: string): string {
  return canonicalWalletName(raw) ?? raw
}

/**
 * Append " wallet" to a display name UNLESS it already contains the word.
 *
 * Fixer pass, G-2 — extracted from `WalletModal`'s former inline guard (`pendingWalletLabel`,
 * `/\bwallet\b/i` test) so the noun-doubling rule ("Coinbase Wallet wallet") has exactly ONE
 * definition instead of two independently-drifting copies. `WalletModal` and `WalletChip` both call
 * this now. BEHAVIOUR-PRESERVING for `WalletModal`: identical regex, identical output for every input
 * it was already handling — this is a lift, not a rewrite.
 */
export function appendWalletSuffix(name: string): string {
  return /\bwallet\b/i.test(name) ? name : `${name} wallet`
}

/**
 * The `WalletChip` accessible-name's wallet-name half (fixer pass, G-1 / G-2).
 *
 * Shares the SAME transport rule `buildRecipientWalletLabel` applies to the review-card label (A5):
 * a WalletConnect session names the TRANSPORT, not the wallet, because Rainbow / Trust / Rabby /
 * Ledger / Safe all arrive through it and all self-report the identical connector name — asserting
 * "WalletConnect wallet" would tell a Rainbow user the app is their wallet. Unlike the review-card
 * label this deliberately omits the network half: the chip's accessible name answers "which wallet",
 * the network belongs to the review card one screen later. Also shares `appendWalletSuffix` above, so
 * a canonical name that already ends in "wallet" (`Coinbase Wallet`) is never suffixed twice.
 *
 * `null` in (unknown connector, or no `walletKind` at all — H6), `null` out. The caller supplies its
 * own truthful fallback (`WalletChip` renders `Connected wallet`); this function must not guess one.
 *
 * @param walletKind the canonical `WALLET_LOGO_KEY` name (`WalletChip`'s own `walletKind` prop is
 *   already the output of `walletLogoKind`/`canonicalWalletName` upstream — never a raw connector
 *   string), or `null`/`undefined` when unmapped or absent.
 */
export function walletChipAriaName(walletKind: string | null | undefined): string | null {
  if (!walletKind) return null
  if (walletKind === WALLETCONNECT_NAME) return `via ${WALLETCONNECT_NAME}`
  return appendWalletSuffix(walletKind)
}

/**
 * The destination network's display name, e.g. `robinhood` → `Robinhood`, `bnb` → `BNB Chain`.
 *
 * @param chain the DESTINATION chain — the network the funds actually land on
 * @param chainNames test seam (H5) — defaults to `CHAIN_REGISTRY`
 */
export function networkDisplayName(
  chain: Chain | null | undefined,
  chainNames: Partial<Record<Chain, string>> = mapChainNames(),
): string | null {
  if (!chain) return null
  return chainNames[chain] ?? null
}

function mapChainNames(): Partial<Record<Chain, string>> {
  const out: Partial<Record<Chain, string>> = {}
  for (const key of Object.keys(CHAIN_REGISTRY) as Chain[]) out[key] = CHAIN_REGISTRY[key].name
  return out
}

export interface RecipientWalletLabelArgs {
  /** The self-reported name of the wallet connected on the DESTINATION chain's family (H7). */
  connectorName: string | null | undefined
  /** The DESTINATION chain — the network half of the label, and the load-bearing half (A1). */
  destinationChain: Chain | null | undefined
  /** Test seam (H5). Both default to the real tables. */
  tables?: {
    nameAllowlist?: ReadonlyArray<readonly [string, string]>
    chainNames?: Partial<Record<Chain, string>>
  }
}

/**
 * Build the review-card recipient label: the WALLET and the NETWORK.
 *
 * ── THE NETWORK HALF IS THE LOAD-BEARING HALF (ADDENDUM A1) ──────────────────────────────────────
 * The parent spec asked for the wallet TYPE alone — `(MetaMask)`. That fixes less than it looks like
 * it does. Chain 4663 is not a default MetaMask network and the app only calls
 * `wallet_addEthereumChain` on the SOURCE chain, so a user who reads `(MetaMask)`, opens MetaMask and
 * finds $0.00 has simply moved the "$0.00 is indistinguishable from a loss" moment one app to the
 * left — while feeling MORE certain. The network is what tells them where the funds are. It is also
 * the only half that varies with risk: the wallet name reads identically on a routine
 * `base → arbitrum` self-bridge and on the Robinhood case.
 *
 * ── RETURN CONTRACT ──────────────────────────────────────────────────────────────────────────────
 * A string the caller renders inside parentheses, or `null` meaning "no honest label" — the caller
 * then renders the pre-F3 `(your wallet)` copy. `null` is returned when the destination chain is
 * absent/unknown (there is no network half to state, and half a label is a worse label) and when the
 * connector name is address-shaped (H3).
 *
 * Four shapes, in precedence order:
 *   1. address-shaped name            → `null`                       (refuse — H3)
 *   2. WalletConnect                  → `via WalletConnect · X network`   (names the TRANSPORT — A5)
 *   3. wallet name == network name    → `Name Wallet · X network`     (brand collision — H5)
 *   4. mapped name                    → `Name · X network`
 *   5. unmapped / Injected / absent   → `your connected wallet · X network`   (never a guess — H6)
 */
export function buildRecipientWalletLabel(args: RecipientWalletLabelArgs): string | null {
  const { connectorName, destinationChain, tables } = args
  const networkName = networkDisplayName(destinationChain, tables?.chainNames ?? mapChainNames())
  // No network → no label. Degrade to the pre-F3 copy rather than printing "MetaMask · undefined
  // network", which is the exact class of half-rendered string this row must never carry.
  if (!networkName) return null

  const raw = connectorName?.trim() ?? ''
  // H3 — refuse before any naming happens.
  if (raw && looksLikeAddress(raw)) return null

  const canonical = canonicalWalletName(raw, tables?.nameAllowlist ?? WALLET_NAME_ALLOWLIST)

  // H6 — unknown/Injected/absent. Provider-agnostic, and still carries the network.
  if (!canonical) return `your connected wallet · ${networkName} network`

  // A5 — WalletConnect is the TRANSPORT, not the wallet. Rainbow / Trust / Rabby / Ledger / Safe all
  // arrive through it and all report the same name, so `(WalletConnect)` would read as a wallet the
  // user does not have. `via` is what stops it parsing as a name. Deriving the real peer behind a WC
  // session is a separate, deferred effort and explicitly out of scope here.
  if (canonical === WALLETCONNECT_NAME) return `via ${WALLETCONNECT_NAME} · ${networkName} network`

  // H5 — brand collision. A wallet app whose name IS the destination brand (a "Robinhood" wallet on
  // the Robinhood chain) would render a bare `(Robinhood)`, reinstating the precise "this is my
  // Robinhood account" reassurance F3 exists to remove. The ` Wallet` suffix makes it read as the
  // wallet APP. Unreachable with today's tables (the WALLET_LOGO_KEY ∩ CHAIN_REGISTRY-names
  // intersection is empty), which is why it is pinned against injected tables — see
  // walletLabel.brandCollision.test.ts.
  if (canonical.toLowerCase() === networkName.toLowerCase()) {
    return `${canonical} Wallet · ${networkName} network`
  }

  return `${canonical} · ${networkName} network`
}

/**
 * H3, applied at the RENDER boundary rather than only at the build boundary.
 *
 * `buildRecipientWalletLabel` already refuses address-shaped input, but the modal receives a plain
 * `string` prop and cannot know who produced it. This is the guard that makes the refusal a property
 * of the recipient ROW instead of a property of one particular caller — the mis-wire it defends
 * against (someone passing `reviewRecipientAddress`, or a future second recipient string, into the
 * label prop) happens at the call site, which is exactly where the builder's own check is bypassed.
 *
 * Splits on whitespace AND the `·` separator so an address embedded in an otherwise well-formed
 * label (`0x4C9B79…b181 · Base network`) is caught, not just a bare address — INCLUDING a truncated
 * one: `looksLikeAddress` treats any `…`/`...` as address-shaped (fixer pass, F-2), so
 * `shortAddr`'s own 8+…+8 output is refused here exactly like the full-length form would be.
 */
export function isRenderableWalletLabel(label: string | null | undefined): boolean {
  if (!label) return false
  const trimmed = label.trim()
  if (!trimmed) return false
  return !trimmed.split(/[\s·]+/).some((token) => token && looksLikeAddress(token))
}
